/**
 * `MentaErrorCode` — frozen taxonomy per `sdd/menta-checkout-sdk-poc/spec`.
 *
 * Async codes (rejected `createCardToken()` promise): `invalid_card`,
 * `not_ready`, `unauthorized`, `network_error`, `timeout`, `unknown`.
 * Sync-throw codes (constructor / `mount()`): `invalid_options`,
 * `invalid_container`.
 *
 * `unauthorized` is currently DEAD CODE — no real `src/inputs`
 * `card-error` signal distinguishes an HTTP 401/403 from any other backend
 * failure, so nothing in this SDK ever produces it today. See docs/protocol.md.
 */
export type MentaErrorCode =
  | 'invalid_card'
  | 'not_ready'
  | 'unauthorized'
  | 'network_error'
  | 'timeout'
  | 'unknown'
  | 'invalid_options'
  | 'invalid_container'
  /** `menta.destroy()` was already called (Engram odd/sdk-options/tasks,
   * item 4) — thrown by `fields.create()`/`fields.createCardToken()`
   * afterward. This is an SDK-internal code (never a wire `CardErrorCode`),
   * so adding it here is not a protocol change. */
  | 'destroyed'
  /** The coordinator never completed the `parent-hello` handshake within the
   * SDK's handshake timeout (unknown site key, origin not allowed for it,
   * blocked/failed coordinator document, network...). Deliberately generic:
   * the SDK cannot and must not reveal WHY. SDK-internal, never a wire
   * `CardErrorCode`. Emitted on every field handle's `error` event. */
  | 'handshake_failed'
  /** The advisory site-status pre-check said this site key does not exist
   * or this page origin is not allowed for it. No iframe was inserted.
   * SDK-internal, never a wire `CardErrorCode`; the `error` event carries
   * `reason` (`unknown_site_key` | `origin_not_allowed`). */
  | 'site_not_authorized';

/**
 * `MentaError` — the SDK's single error type, both for synchronous
 * constructor/`mount()` throws and for `createCardToken()` promise rejections.
 * Shape matches the real `sdk.mercadopago.com/js/v2` structured error
 * catalog pattern (`{ code, message }`), not a bare native `Error`.
 */
export class MentaError extends Error {
  readonly code: MentaErrorCode;
  readonly cause?: unknown;

  constructor(code: MentaErrorCode, message: string, cause?: unknown) {
    super(message);
    this.name = 'MentaError';
    this.code = code;
    this.cause = cause;
  }
}
